import {
  $applyNodeReplacement,
  type DOMConversionMap,
  type DOMExportOutput,
  type EditorConfig,
  LexicalEditor,
  type NodeKey,
  type SerializedTextNode,
  TextNode,
} from "lexical";

import { INLINE_FORMATS } from "./formats";

type SerializedTypographyTextNode = Omit<SerializedTextNode, "type"> & { type: "typography-text" };

const buildFormattedElementChain = (node: TypographyTextNode): { outer: HTMLElement; innermost: HTMLElement } | null => {
  const textNode = document.createTextNode(node.getTextContent());
  let outer: HTMLElement | null = null;
  let innermost: HTMLElement | null = null;
  for (const { format, tag, className } of INLINE_FORMATS) {
    if (!node.hasFormat(format)) continue;
    const wrapper = document.createElement(tag);
    wrapper.className = className;
    wrapper.appendChild(outer ?? textNode);
    if (innermost === null) innermost = wrapper;
    outer = wrapper;
  }
  if (outer === null || innermost === null) return null;
  return { outer, innermost };
};

const getInnermostElement = (dom: HTMLElement): HTMLElement => {
  let current = dom;
  while (current.firstElementChild instanceof HTMLElement) current = current.firstElementChild;
  return current;
};

export class TypographyTextNode extends TextNode {
  static getType(): string {
    return "typography-text";
  }

  static clone(prev: TypographyTextNode): TypographyTextNode {
    const next = new TypographyTextNode(prev.__text, prev.__key);
    next.__format = prev.__format;
    next.__detail = prev.__detail;
    next.__mode = prev.__mode;
    next.__style = prev.__style;
    return next;
  }

  static importJSON({ text, format, detail, mode, style }: SerializedTypographyTextNode): TypographyTextNode {
    const node = $createTypographyTextNode(text);
    node.setFormat(format);
    node.setDetail(detail);
    node.setMode(mode);
    node.setStyle(style);
    return node;
  }

  exportJSON(): SerializedTypographyTextNode {
    return { ...super.exportJSON(), type: "typography-text" };
  }

  /* Pairs with the custom exportDOM below. Reuse TextNode's DOM conversions so
     pasted HTML imports as text nodes, which the editor's replace override then
     re-wraps as TypographyTextNode. */
  static importDOM(): DOMConversionMap | null {
    return TextNode.importDOM();
  }

  createDOM(_config: EditorConfig, _editor?: LexicalEditor): HTMLElement {
    const chain = buildFormattedElementChain(this);
    const css = this.__style;
    if (chain) {
      if (css !== "") chain.outer.style.cssText = css;
      return chain.outer;
    }
    const span = document.createElement("span");
    if (css !== "") span.style.cssText = css;
    span.appendChild(document.createTextNode(this.getTextContent()));
    return span;
  }

  updateDOM(prev: TypographyTextNode, dom: HTMLElement, _config: EditorConfig): boolean {
    if (prev.__format !== this.__format) return true;
    const css = this.__style;
    if (prev.__style !== css) dom.style.cssText = css;
    const innermost = getInnermostElement(dom);
    const nextText = this.getTextContent();
    const firstChild = innermost.firstChild;
    if (firstChild instanceof Text) {
      if (firstChild.nodeValue !== nextText) firstChild.nodeValue = nextText;
    } else {
      innermost.textContent = nextText;
    }
    return false;
  }

  exportDOM(_editor: LexicalEditor): DOMExportOutput {
    const chain = buildFormattedElementChain(this);
    if (chain) return { element: chain.outer };
    return { element: document.createTextNode(this.getTextContent()) };
  }
}

export const $createTypographyTextNode = (text: string, key?: NodeKey): TypographyTextNode => {
  return $applyNodeReplacement(new TypographyTextNode(text, key));
};
